/**
 * @file usePickerFinalFocus.ts
 * @description Куда уходит фокус после закрытия диалога «Подбор»
 * (phase19.24 этап 1, шаг 1; дефект повторного QA 2026-09-20).
 *
 * base-ui возвращает фокус на триггер ПОСЛЕ закрытия — позже, чем панель
 * успевает позвать `focus()` у следующего поля. В приёмке и списании триггера
 * «Подбор» к этому моменту уже нет (на его месте карточка выбранной фасовки),
 * и фокус падал в `body`: кладовщик вводил количество «в никуда».
 *
 * Поэтому цель фокуса задаёт сам диалог через `finalFocus`:
 *   - строку выбрали → следующее поле панели («Количество», «Партия»);
 *   - закрыли без выбора (Esc, крестик, «перейти к строке») → `false`: фокус
 *     остаётся там, куда его поставил экран (поле скана в `onClose`).
 *
 * Один хелпер на три диалога подбора (`WmsSkuSelectDialog`,
 * `TransferSkuPickerDialog`, `PickItemPickerDialog`) — иначе каждый заводил бы
 * свой порядок фокуса, и соседние экраны склада вели бы себя по-разному.
 */

import { useCallback, useRef, type RefObject } from "react";

export interface PickerFinalFocus {
  /** Диалог: строку выбрали — фокус уйдёт в следующее поле панели. */
  markPicked: () => void;
  /** Значение пропа `finalFocus` для `SheetContent` / `DialogContent`. */
  finalFocus: () => HTMLElement | false;
}

export function usePickerFinalFocus(
  nextField?: RefObject<HTMLElement | null>,
): PickerFinalFocus {
  const picked = useRef(false);

  const markPicked = useCallback(() => {
    picked.current = true;
  }, []);

  const finalFocus = useCallback(() => {
    // Флаг одноразовый: следующее открытие диалога начинается «без выбора».
    const target = picked.current ? nextField?.current : null;
    picked.current = false;
    return target ?? false;
  }, [nextField]);

  return { markPicked, finalFocus };
}
